/**
* /branches — ศูนย์รวมจุดบริการทั้งเครือข่าย
*
* GOOGLE COMPLIANCE:
* - หน้านี้เป็น "hub" หน้าเดียวที่รวมทุกจุด — เราเจตนา *ไม่* สร้างหน้า
* /branches/$id แยกรายจุดหลายร้อยหน้า เพราะจะกลายเป็น doorway pages
* (เนื้อหาซ้ำต่างกันแค่ชื่อสถานที่) ซึ่งผิดนโยบาย Google Search Spam
* - มีเพียงสำนักงานใหญ่เท่านั้นที่ประกาศ PostalAddress / LocalBusiness
* จุดอื่นทั้งหมดคือ areaServed (นัดหมายล่วงหน้า / นอกสถานที่)
*/
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE, absoluteUrl } from "@/config/sites";
import { buildHreflangLinks } from "@/lib/hreflang";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedClusters } from "@/components/RelatedClusters";
import {
  ALL_BRANCHES,
  OFFICE_BRANCHES,
  SERVICE_AREA_BRANCHES,
  BRANCH_TYPE_LABEL,
  locationLine,
  type Branch,
  type BranchType,
} from "@/data/branches/index";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  organizationSchema,
  faqPageSchema,
  localBusinessSchema,
} from "@/lib/schema";

const TITLE = "จุดบริการ Notary Public ทั่วไทย — เครือข่ายนัดหมายนอกสถานที่";
const DESC =
  "รายชื่อจุดนัดพบและพื้นที่ให้บริการ Notary Public แปลรับรอง และรับรองกงสุลทั่วประเทศไทย ครอบคลุมกรุงเทพฯ ทุกเขต สถานีรถไฟฟ้า ศูนย์การค้า และทุกจังหวัด นัดหมายล่วงหน้าได้ทุกวัน";

const FAQS = [
  {
    question: "จุดบริการเหล่านี้เป็นสำนักงานสาขาถาวรหรือไม่?",
    answer:
      "ไม่ใช่ทั้งหมดครับ สำนักงานจดทะเบียนที่มีที่อยู่จริงของเราคือสำนักงานใหญ่ที่เขตวังทองหลาง กรุงเทพมหานคร ส่วนจุดอื่น ๆ ที่ระบุไว้เป็นจุดนัดพบสำหรับให้บริการนอกสถานที่แบบนัดหมายล่วงหน้า เราแสดงข้อมูลตามความเป็นจริงเพื่อไม่ให้ลูกค้าเดินทางผิดที่",
  },
  {
    question: "นัดหมายนอกสถานที่มีค่าใช้จ่ายเพิ่มไหม?",
    answer:
      "ค่าบริการหลักเท่ากับที่สำนักงาน โดยมีค่าเดินทางตามระยะทางจริงซึ่งจะแจ้งให้ทราบก่อนยืนยันนัดหมายเสมอ ไม่มีการเรียกเก็บเพิ่มภายหลัง",
  },
  {
    question: "ถ้าจังหวัดของฉันไม่มีในรายการต้องทำอย่างไร?",
    answer:
      "เราให้บริการครบทั้ง 77 จังหวัด หากไม่พบจุดนัดพบใกล้คุณ สามารถส่งเอกสารทางไปรษณีย์ EMS หรือใช้บริการนัดหมายวิดีโอคอลเพื่อเตรียมเอกสารก่อนลงนามได้",
  },
  {
    question: "ต้องเตรียมอะไรไปในวันนัด?",
    answer:
      "เตรียมบัตรประชาชนหรือหนังสือเดินทางฉบับจริง เอกสารต้นฉบับที่ต้องรับรอง และหากเป็นนิติบุคคลให้นำหนังสือรับรองบริษัทอายุไม่เกิน 6 เดือนมาด้วย",
  },
];

const TYPE_ORDER: BranchType[] = [
  "hq",
  "district",
  "province",
  "mall",
  "airport",
  "border",
  "government",
  "visa-center",
  "expat-hub",
  "marina",
  "overseas",
];

export const Route = createFileRoute("/branches")({
  component: BranchesHub,
  loader: () => ({ h: (() => {
    const url = absoluteUrl("/branches");
    return {
      meta: [
        { title: ctrTitle(TITLE) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: absoluteUrl(SITE.logoPath) },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks("/branches")],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            organizationSchema(),
            webPageSchema({ url, title: TITLE, description: DESC, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: absoluteUrl("/") },
              { name: "จุดบริการ", url },
            ]),
            faqPageSchema(FAQS),
            ...OFFICE_BRANCHES.map((b) =>
              localBusinessSchema({
                id: b.id,
                name: b.name,
                street: b.street,
                locality: b.locality,
                region: b.region,
                postalCode: b.postalCode,
                country: b.country,
                phone: b.phone,
                lat: b.geo?.lat ?? 0,
                lng: b.geo?.lng ?? 0,
                mapUrl: b.mapUrl,
              }),
            ),
            {
              "@type": "ItemList",
              name: "จุดนัดพบและพื้นที่ให้บริการ",
              numberOfItems: ALL_BRANCHES.length,
              itemListElement: ALL_BRANCHES.slice(0, 200).map((b, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: b.name,
                description: locationLine(b),
              })),
            },
          ]),
        ),
      ],
    };
  })() }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

function groupByType(list: Branch[]) {
  return TYPE_ORDER.map((t) => [t, list.filter((b) => b.branchType === t)] as const).filter(
    ([, arr]) => arr.length > 0,
  );
}

function BranchesHub() {
  const groups = groupByType(SERVICE_AREA_BRANCHES);

  return (
    <>
      <PageHero
        eyebrow="เครือข่ายจุดบริการ"
        title="จุดบริการ Notary Public และแปลรับรองทั่วประเทศไทย"
        subtitle={`สำนักงานจริง ${OFFICE_BRANCHES.length} แห่ง และจุดนัดพบนอกสถานที่อีก ${SERVICE_AREA_BRANCHES.length} จุด ครอบคลุมกรุงเทพฯ ทุกเขต สถานีรถไฟฟ้า ศูนย์การค้า ด่านชายแดน และทุกจังหวัด`}
        badges={["Notarial Services Attorney", "นัดหมายล่วงหน้า", "บริการนอกสถานที่", "EMS ทั่วประเทศ"]}
      />

      <Breadcrumbs jsonLd items={[{ label: "หน้าแรก", to: "/" }, { label: "จุดบริการ" }]} />

      <section className="mx-auto max-w-6xl px-4 py-16 cv-auto">
        <div className="rounded-lg border border-border/60 bg-muted/30 p-5">
          <p className="text-sm answer-summary">
            <strong>อ่านก่อนเดินทาง:</strong> สำนักงานที่มีที่อยู่จดทะเบียนจริงของเรามี 3 แห่ง
            คือ กรุงเทพฯ (วังทองหลาง), ขอนแก่น และอุดรธานี ตามรายการด้านล่าง
            จุดอื่นทั้งหมดในหน้านี้คือ “จุดนัดพบ” สำหรับบริการนอกสถานที่ซึ่งต้องนัดหมายล่วงหน้า
            เราไม่ประกาศที่อยู่ที่เราไม่ได้ตั้งสำนักงานจริง
          </p>
        </div>

        <h2 className="mt-10 text-2xl font-semibold">สำนักงานจริง</h2>
        <ul className="mt-4 grid gap-4 md:grid-cols-2">
          {OFFICE_BRANCHES.map((b) => (
            <li key={b.id} className="rounded-lg border border-border/60 bg-card p-5">
              <h3 className="font-semibold text-foreground">{b.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{locationLine(b)}</p>
              {b.landmarks.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">ใกล้: {b.landmarks.join(" · ")}</p>
              )}
              {b.transit.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">เดินทาง: {b.transit.join(" · ")}</p>
              )}
              <p className="mt-2 text-sm">โทร {b.phone}</p>
              <p className="text-sm">เปิด {b.hours}</p>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <Link to="/branches/$branch" params={{ branch: b.id }} className="underline">
                  ดูรายละเอียดสาขา →
                </Link>
                <Link to="/contact" className="underline">
                  นัดหมาย →
                </Link>
                {b.mapUrl && (
                  <a
                    href={b.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    ดูแผนที่ Google Maps →
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>

        {groups.map(([type, list]) => (
          <div key={type} className="mt-12">
            <h2 className="text-2xl font-semibold">
              {BRANCH_TYPE_LABEL[type]}{" "}
              <span className="text-base font-normal text-muted-foreground">({list.length} จุด)</span>
            </h2>
            <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {list.map((b) => (
                <li key={b.id} className="text-muted-foreground">
                  <span className="text-foreground">{b.shortName || b.name}</span>
                  {b.meetingPoint ? ` — ${b.meetingPoint}` : ""}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <h2 className="mt-12 text-2xl font-semibold">คำถามที่พบบ่อยเรื่องจุดบริการ</h2>
        <dl className="mt-4 space-y-3">
          {FAQS.map((f) => (
            <div key={f.question} className="rounded-lg border border-border/60 bg-card p-4">
              <dt className="font-medium text-foreground">{f.question}</dt>
              <dd className="mt-2 text-sm text-muted-foreground faq-answer">{f.answer}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-8 text-sm text-muted-foreground">
          ดูรายละเอียดบริการรายพื้นที่ได้ที่{" "}
          <Link to="/coverage" className="underline">
            หน้าพื้นที่ให้บริการ
          </Link>
        </p>
      </section>

      <div className="mx-auto max-w-6xl px-4">
        <RelatedClusters />
      </div>
    </>
  );
}
